import "./NpsSurvey.scss";

import { Alert, Button, Flex, Text, TextArea } from "@fluentui/react-northstar";
import { dialog } from "@microsoft/teams-js";
import { UserDTO } from "@teams/types/api";
import React, { useEffect, useState } from "react";

import { useUserInfo } from "../../../_shared/actions/useUserInfo";
import PollyFlapSquawkAnimation from "../../../_shared/assets/polly-flap-squawk-animation.svg";
import {
  HUBSPOT_FORM_SUBMIT_ENDPOINT_URL,
  HUBSPOT_NPS_FORM_ID,
  HUBSPOT_OBJECT_TYPE_ID,
  HUBSPOT_PORTAL_ID,
} from "../../../_shared/constants/Nps";
import { FetchResult, fetchNoToken, isError } from "../../../_shared/services/ChannelApi";
import { userApi } from "../../../_shared/services/UserApi";
import { useTeamsContext } from "../../../_shared/state/TeamsContextProvider";
import { isMobileClient } from "../../../_shared/utils/clientHelper";
import { clientEventTracker } from "../../../_shared/utils/clientTelemetry";

export const NpsSurvey: React.FC = () => {
  const rankNumbers = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  const [optOut, setOptOut] = useState<boolean>(false);

  const [isSubmitLoading, setIsSubmitLoading] = useState<boolean>(false);
  const [isOptoutLoading, setIsOptoutLoading] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | undefined>(undefined);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [selectedRank, setSelectedRank] = useState<number | undefined>(undefined);
  const [npsReason, setNpsReason] = useState<string>();
  const { teamsContext } = useTeamsContext();
  const { user } = useUserInfo();
  const isMobile = isMobileClient(teamsContext);

  useEffect(() => {
    if (optOut) {
      dialog.update.resize({ height: 350, width: 436 });
    } else if (submitted) {
      dialog.update.resize({ height: 400, width: 701 });
    } else if (selectedRank !== undefined) {
      dialog.update.resize({ height: 480, width: 701 });
    }
  }, [optOut, selectedRank, submitted]);

  return (
    <div className="survey-container">
      {optOut ? (
        <SurveyOptOutStateComponent />
      ) : submitted ? (
        <SurveySubmittedStateComponent />
      ) : (
        <Flex column space="between" styles={{ height: "100%" }}>
          <Flex className="survey-content" column hAlign="start" gap="gap.large">
            <Text className="survey-content__heading">
              👋 We would love to hear your candid feedback about Polly
            </Text>
            <Flex column hAlign="start" gap="gap.medium">
              <Text className="survey-content__sub-heading">
                How likely are you to recommend Polly to a friend or colleague?
              </Text>
              <Flex className="survey-content__rank-container" column gap="gap.medium">
                <Flex gap={isMobile ? "gap.small" : "gap.medium"}>
                  {rankNumbers.map(number => {
                    return (
                      <SurveyRankNumberComponent
                        key={number.toString()}
                        rank={number}
                        selectedRank={selectedRank}
                        user={user}
                        setSelectedRank={setSelectedRank}
                      />
                    );
                  })}
                </Flex>
                <Flex space="between">
                  <Text className="survey-content__sub-title">0 - Strong disagree</Text>
                  <Flex.Item push>
                    <Text className="survey-content__sub-title">10 - Strongly agree</Text>
                  </Flex.Item>
                </Flex>
              </Flex>
            </Flex>
            {selectedRank !== undefined && (
              <>
                <Flex
                  className="survey-content__reason-container"
                  column
                  hAlign="start"
                  gap="gap.medium"
                >
                  <Text className="survey-content__sub-heading required">
                    Please tell us why you gave this rating <Text content="*" color="red" />
                  </Text>
                  <TextArea
                    className="survey-content__text-area"
                    fluid
                    onChange={e => {
                      setNpsReason((e.target as HTMLInputElement).value);
                    }}
                  />
                </Flex>
                <Flex.Item push>
                  <Flex hAlign="end" vAlign="end" fill>
                    <Button
                      className="survey-content__button nps-button"
                      content="Submit"
                      loading={isSubmitLoading}
                      onClick={async () => {
                        setIsSubmitLoading(true);

                        let hubspotSubmissionError = undefined;
                        try {
                          const hubSpotResponse = await submitHubSpotForm(
                            user,
                            selectedRank,
                            npsReason
                          );
                          if (isError(hubSpotResponse)) {
                            hubspotSubmissionError = hubSpotResponse;
                          }
                        } catch (e) {
                          hubspotSubmissionError = e;
                        } finally {
                          if (hubspotSubmissionError) {
                            setSubmitError(
                              "Oops, something went wrong please try again or contact support"
                            );
                            clientEventTracker.trackFatalError("nps/hubspot-form-error", user, {
                              error: hubspotSubmissionError,
                            });

                            return;
                          }
                        }

                        const submitResponse = await userApi.updateUserSubmittedAtForNps(
                          teamsContext
                        );

                        if (isError(submitResponse)) {
                          setSubmitError(
                            "Oops, something went wrong please try again or contact support"
                          );
                          clientEventTracker.trackFatalError("nps/submit-error", user, {
                            error: submitResponse,
                          });

                          return;
                        }

                        setIsSubmitLoading(false);
                        clientEventTracker.track("NPS submitted", user, {
                          source: "Teams",
                          score: selectedRank,
                          reason: npsReason,
                        });
                        setSubmitted(true);
                      }}
                    />
                  </Flex>
                </Flex.Item>
              </>
            )}
            {submitError && (
              <Alert className="survey-content__submit-error" content={submitError} danger />
            )}
          </Flex>
          <Flex.Item push>
            <Flex className="survey-footer" hAlign="center" vAlign="center" gap="gap.small">
              <Text className="survey-footer__title">
                <Button
                  text
                  loading={isOptoutLoading}
                  className="survey-footer__title survey-footer__title-button"
                  onClick={async () => {
                    setIsOptoutLoading(true);
                    const response = await userApi.updateUserOptOutForNps(teamsContext, true);
                    setIsOptoutLoading(false);
                    if (isError(response)) {
                      console.error(response);
                      return;
                    }
                    setOptOut(true);
                    clientEventTracker.track(
                      "NPS opted out",
                      user,
                      {
                        source: "Teams",
                      },
                      { "nps.optout": true }
                    );
                  }}
                >
                  Opt out
                </Button>{" "}
                from future feedback surveys.
              </Text>
            </Flex>
          </Flex.Item>
        </Flex>
      )}
    </div>
  );
};

const SurveyRankNumberComponent: React.FC<{
  rank: number;
  selectedRank?: number;
  user: UserDTO;

  setSelectedRank: (rank: number) => void;
}> = ({ rank, selectedRank, user, setSelectedRank }) => {
  return (
    <div
      className={`rank-number ${rank === selectedRank ? "selected" : ""}`}
      onClick={async () => {
        setSelectedRank(rank);
        clientEventTracker.track("NPS clicked", user, {
          source: "Teams",
          score: rank,
        });
      }}
    >
      {rank}
    </div>
  );
};

const SurveySubmittedStateComponent: React.FC<{}> = () => {
  return (
    <Flex
      className="survey-content"
      column
      hAlign="center"
      gap="gap.large"
      styles={{ height: "100%" }}
    >
      <img src={PollyFlapSquawkAnimation} height={100} alt="" />

      <Text className="survey-content__heading" align="center">
        Thank you for your feedback 🙏
      </Text>
      <Text className="survey-content__sub-heading" align="center">
        Polly will keep getting better with feedback like yours 💙 If there’s anything more you’d
        like to share, please reach out through{" "}
        <a
          href="https://www.polly.ai/contact"
          className="survey-content__link"
          target="_blank"
          rel="noreferrer"
        >
          contact us
        </a>
      </Text>
      <Flex.Item push>
        <Flex hAlign="end" vAlign="end" fill styles={{ marginRight: "-75px" }}>
          <Button
            className="nps-button"
            content="Dismiss"
            onClick={() => {
              dialog.url.submit();
            }}
          />
        </Flex>
      </Flex.Item>
    </Flex>
  );
};

const SurveyOptOutStateComponent: React.FC<{}> = () => {
  return (
    <Flex
      className="survey-content"
      column
      hAlign="center"
      gap="gap.large"
      space="between"
      styles={{ height: "100%" }}
    >
      <Text
        className="survey-content__heading"
        align="center"
        content={"Success! You have been removed from future feedback surveys."}
      />
      <img src={PollyFlapSquawkAnimation} height={100} alt="" />
      <Flex.Item push>
        <Flex hAlign="end" vAlign="end" fill styles={{ margin: "0 20px 20px 0px;" }}>
          <Button
            className="nps-button"
            content="Dismiss"
            onClick={() => {
              dialog.url.submit();
            }}
          />
        </Flex>
      </Flex.Item>
    </Flex>
  );
};

const submitHubSpotForm = async (user: UserDTO, rank: number, reason?: string) => {
  const result = await fetchNoToken<FetchResult<void>>(
    `${HUBSPOT_FORM_SUBMIT_ENDPOINT_URL}/${HUBSPOT_PORTAL_ID}/${HUBSPOT_NPS_FORM_ID}`,
    {
      method: "POST",
      body: JSON.stringify({
        fields: [
          {
            objectTypeId: HUBSPOT_OBJECT_TYPE_ID,
            name: "email",
            value: user.upn,
          },
          {
            objectTypeId: HUBSPOT_OBJECT_TYPE_ID,
            name: "nps_score",
            value: rank,
          },
          {
            objectTypeId: HUBSPOT_OBJECT_TYPE_ID,
            name: "nps_reason",
            value: reason ?? "",
          },
          {
            objectTypeId: HUBSPOT_OBJECT_TYPE_ID,
            name: "firstname",
            value: user.name.split(" ")[0] || "",
          },
          {
            objectTypeId: HUBSPOT_OBJECT_TYPE_ID,
            name: "lastname",
            value: user.name.split(" ")[1] || "",
          },
          {
            objectTypeId: HUBSPOT_OBJECT_TYPE_ID,
            name: "full_name",
            value: user.name,
          },
        ],
      }),
    }
  );
  return result;
};
